import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MessageSearchResult } from '../../types/chat';
import { chatAPI } from '../../services/api';
import ChatAvatar from './ChatAvatar';

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

interface MessageSearchProps {
  onSelectResult: (conversationId: number, messageId: number) => void;
  onClose?: () => void;
}

const MessageSearch: React.FC<MessageSearchProps> = ({ onSelectResult, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MessageSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await chatAPI.searchMessages(searchQuery.trim());
      setResults(response.results);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer for debounced search
    const timer = setTimeout(() => {
      handleSearch(value);
    }, 300);
    setDebounceTimer(timer);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isThisYear = date.getFullYear() === now.getFullYear();

      if (isToday) {
        return format(date, 'HH:mm', { locale: fr });
      } else if (isThisYear) {
        return format(date, 'd MMM', { locale: fr });
      } else {
        return format(date, 'd MMM yyyy', { locale: fr });
      }
    } catch {
      return '';
    }
  };

  const highlightMatch = (text: string | null, searchQuery: string) => {
    if (!text || !searchQuery.trim()) return text;

    const regex = new RegExp(`(${searchQuery.trim()})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#fff59d', fontWeight: 500 }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher dans les messages..."
            value={query}
            onChange={handleQueryChange}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClear}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.default',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-focused': {
                  backgroundColor: 'background.paper',
                },
              },
            }}
          />
          {onClose && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Results */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress size={32} sx={{ color: OUTLOOK_BLUE }} />
          </Box>
        ) : results.length > 0 ? (
          <List disablePadding>
            {results.map((result) => (
              <ListItem
                key={result.id}
                onClick={() => onSelectResult(result.conversation_id, result.id)}
                sx={{
                  cursor: 'pointer',
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'background.default',
                  },
                }}
              >
                <ListItemAvatar>
                  {result.is_group ? (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: OUTLOOK_BLUE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <GroupIcon sx={{ color: 'white' }} />
                    </Box>
                  ) : (
                    <ChatAvatar
                      name={result.sender?.full_name || 'Unknown'}
                      size={40}
                      showStatus={false}
                    />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 500 }}>
                        {result.conversation_name || 'Conversation'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1, flexShrink: 0 }}>
                        {formatDate(result.created_at)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: OUTLOOK_BLUE, fontWeight: 500 }}
                      >
                        {result.sender?.full_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {highlightMatch(result.content, query)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : searched && !loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              px: 2,
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Aucun message trouvé
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
              Essayez avec d'autres mots-clés
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              px: 2,
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Rechercher des messages
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
              Tapez un mot-clé pour rechercher
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MessageSearch;
